import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../../api/client'

const BACKEND_POLL_INTERVAL_BASE_MS = 10_000
const BACKEND_POLL_INTERVAL_HIDDEN_MS = 30_000
const JITTER_MAX_MS = 2_000

const AGENT_STATUS_NORMAL_POLL_INTERVAL_MS = 10_000
const AGENT_STATUS_WARNING_POLL_INTERVAL_MS = 1_000
const AGENT_STATUS_BACKOFF_POLL_INTERVAL_MS = 30_000
const AGENT_STATUS_TIMEOUT_MS = 900
const COUNTDOWN_SECONDS = 0

const BROADCAST_CHANNEL_NAME = 'proctoring_status_sync'

const VALID_PROCTORING_STATES = new Set([
  'ACTIVE',
  'INACTIVE',
  'NOT_REQUIRED',
  'SYSTEM_FAILURE',
  'NO_SESSION',
])

function normalizeProctoringState(data) {
  const serverState = String(data?.proctoringState || '').trim().toUpperCase()
  if (VALID_PROCTORING_STATES.has(serverState)) return serverState
  if (data?.sessionActive) return 'ACTIVE'
  return 'INACTIVE'
}

function normalizeReason(reason, fallback = 'NO_SESSION') {
  const normalized = String(reason || '').trim().toUpperCase()
  return normalized || fallback
}

function getAgentPort() {
  const raw = sessionStorage.getItem('agentPort')
  const parsed = Number.parseInt(String(raw || '').trim(), 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

async function fetchAgentStatus(port) {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), AGENT_STATUS_TIMEOUT_MS)
  try {
    const response = await fetch(`http://localhost:${port}/status`, {
      signal: controller.signal,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) throw new Error(`Agent status failed: HTTP ${response.status}`)
    return await response.json()
  } finally {
    window.clearTimeout(timeout)
  }
}

export function useProctoringStatusMonitor({ onSessionEnded, enabled = true }) {
  const onSessionEndedRef = useRef(onSessionEnded)
  const showWarningRef = useRef(false)
  const endingSessionRef = useRef(false)
  const sessionEndedRef = useRef(false)
  const agentWarningAvailableRef = useRef(false)
  const lastSyncTimestampRef = useRef(0)

  const [proctoringState, setProctoringState] = useState('INACTIVE')
  const [agentRequired, setAgentRequired] = useState(true)
  const [showWarning, setShowWarning] = useState(false)
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS)
  const [sessionStatusSnapshot, setSessionStatusSnapshot] = useState(null)

  useEffect(() => {
    onSessionEndedRef.current = onSessionEnded
  }, [onSessionEnded])

  useEffect(() => {
    showWarningRef.current = showWarning
  }, [showWarning])

  const endSession = useCallback(async (reason = 'USER_INACTIVE', payload = null) => {
    if (!enabled || endingSessionRef.current || sessionEndedRef.current) return
    endingSessionRef.current = true
    sessionEndedRef.current = true
    try {
      await onSessionEndedRef.current?.(reason, payload)
    } finally {
      endingSessionRef.current = false
    }
  }, [])

  // ── Apply incoming status snapshot (from server or broadcast channel) ────────
  const applyStatusData = useCallback(
    async (data) => {
      if (!data) return
      setSessionStatusSnapshot(data)
      setProctoringState(normalizeProctoringState(data))
      setAgentRequired(Boolean(data?.agentRequired ?? true))

      if (data?.sessionActive) {
        sessionEndedRef.current = false
      }

      if (!data?.sessionActive) {
        const rawReason = normalizeReason(data?.reason, 'NO_SESSION')
        const normalizedReason =
          rawReason === 'NO_SESSION' && showWarningRef.current ? 'USER_INACTIVE' : rawReason
        setShowWarning(false)
        setCountdown(0)
        if (!sessionEndedRef.current) {
          await endSession(normalizedReason, data)
        }
        return
      }

      if (!agentWarningAvailableRef.current) {
        const fallbackWarning = Boolean(data?.warning)
        if (fallbackWarning) {
          const parsedSeconds = Number(data?.logoutInSeconds)
          const seconds = Number.isFinite(parsedSeconds) ? Math.max(parsedSeconds, 0) : 0
          setShowWarning(true)
          setCountdown(seconds)
        } else if (showWarningRef.current) {
          setShowWarning(false)
          setCountdown(0)
        }
      }
    },
    [endSession]
  )

  // ── BroadcastChannel for multi-tab state deduplication ───────────────────────
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !('BroadcastChannel' in window)) return undefined

    let channel = null
    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
      channel.onmessage = (event) => {
        const { type, payload, timestamp } = event.data || {}
        if (type === 'STATUS_SNAPSHOT' && payload) {
          lastSyncTimestampRef.current = timestamp || Date.now()
          applyStatusData(payload)
        } else if (type === 'WARNING_DISMISSED') {
          setShowWarning(false)
          setCountdown(0)
        }
      }
    } catch {
      // BroadcastChannel unavailable in restricted context
    }

    return () => {
      try {
        channel?.close()
      } catch { }
    }
  }, [applyStatusData])

  const broadcastEvent = useCallback((type, payload = null) => {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      try {
        const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
        channel.postMessage({ type, payload, timestamp: Date.now() })
        channel.close()
      } catch { }
    }
  }, [])

  // ── Local Electron Agent Status Polling Loop ────────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined
    const token = localStorage.getItem('authToken')
    if (!token) return undefined

    let active = true
    let inFlight = false
    let poller = null
    let consecutiveFailures = 0

    const scheduleNextPoll = (delayMs) => {
      if (!active) return
      poller = window.setTimeout(pollAgentStatus, delayMs)
    }

    const pollAgentStatus = async () => {
      if (!active || inFlight) return
      const port = getAgentPort()
      if (!port) {
        agentWarningAvailableRef.current = false
        consecutiveFailures = 0
        scheduleNextPoll(AGENT_STATUS_NORMAL_POLL_INTERVAL_MS)
        return
      }
      inFlight = true
      let nextDelay = AGENT_STATUS_NORMAL_POLL_INTERVAL_MS
      try {
        const data = await fetchAgentStatus(port)
        if (!active) return
        consecutiveFailures = 0
        agentWarningAvailableRef.current = true

        const warningActive = Boolean(data?.isTracking && data?.idleWarningActive)
        const remainingRaw = Number(data?.idleWarningRemainingSeconds)
        const remaining = Number.isFinite(remainingRaw) ? Math.max(remainingRaw, 0) : 0
        nextDelay = warningActive ? AGENT_STATUS_WARNING_POLL_INTERVAL_MS : AGENT_STATUS_NORMAL_POLL_INTERVAL_MS

        if (warningActive) {
          setShowWarning(true)
          setCountdown(remaining)
        } else if (showWarningRef.current) {
          setShowWarning(false)
          setCountdown(0)
        }

        const autoReason = normalizeReason(data?.idleAutoLogoutReason, '')
        const isAutoLogoutSignal = !data?.isTracking && (autoReason === 'USER_INACTIVE' || autoReason === 'SYSTEM_FAILURE')
        if (isAutoLogoutSignal && !sessionEndedRef.current) {
          setShowWarning(false)
          setCountdown(0)
          await endSession(autoReason, { source: 'agent-auto-logout', agentStatus: data })
        }
      } catch {
        consecutiveFailures += 1
        agentWarningAvailableRef.current = false
        nextDelay =
          consecutiveFailures >= 2
            ? AGENT_STATUS_BACKOFF_POLL_INTERVAL_MS
            : AGENT_STATUS_NORMAL_POLL_INTERVAL_MS
      } finally {
        inFlight = false
        scheduleNextPoll(nextDelay)
      }
    }

    pollAgentStatus()
    return () => {
      active = false
      if (poller) window.clearTimeout(poller)
    }
  }, [endSession])

  // ── Backend /session-status Polling Loop (Adaptive Jitter + Deduplication) ───
  useEffect(() => {
    if (!enabled) return undefined
    const token = localStorage.getItem('authToken')
    if (!token) return undefined

    let active = true
    let timerId = null
    let inFlight = false

    const checkStatus = async () => {
      if (!active || inFlight) return

      // If another tab has synced state in the last 8 seconds, skip this HTTP round-trip
      const elapsedSinceLastSync = Date.now() - lastSyncTimestampRef.current
      if (elapsedSinceLastSync < 8_000) {
        scheduleNext()
        return
      }

      inFlight = true
      try {
        const { data } = await api.get('/activity-monitor/session-status')
        if (!active) return

        lastSyncTimestampRef.current = Date.now()
        await applyStatusData(data || null)
        broadcastEvent('STATUS_SNAPSHOT', data || null)
      } catch {
        // Skip cycle on error and retry on next interval
      } finally {
        inFlight = false
        scheduleNext()
      }
    }

    const scheduleNext = () => {
      if (!active) return
      if (timerId) window.clearTimeout(timerId)

      // Page Visibility backoff: slow down polling when tab is hidden
      const isHidden = typeof document !== 'undefined' && document.hidden
      const baseDelay = isHidden
        ? BACKEND_POLL_INTERVAL_HIDDEN_MS
        : BACKEND_POLL_INTERVAL_BASE_MS

      // Add random jitter (0 to 2000ms) to prevent Thunder Herd synchronized waves
      const jitter = Math.floor(Math.random() * JITTER_MAX_MS)
      const nextDelay = baseDelay + jitter

      timerId = window.setTimeout(checkStatus, nextDelay)
    }

    // Immediate check on tab focus/visibility change if data is stale
    const handleVisibilityChange = () => {
      if (!document.hidden && Date.now() - lastSyncTimestampRef.current > BACKEND_POLL_INTERVAL_BASE_MS) {
        checkStatus()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Initial check
    checkStatus()

    return () => {
      active = false
      if (timerId) window.clearTimeout(timerId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [applyStatusData, broadcastEvent])

  const dismissWarning = useCallback(() => {
    setShowWarning(false)
    setCountdown(0)
    broadcastEvent('WARNING_DISMISSED')
  }, [broadcastEvent])

  const isProctoringActive = useMemo(
      () => enabled && agentRequired && proctoringState === 'ACTIVE',
        [enabled, agentRequired, proctoringState]
  )

  return {
    proctoringState: enabled ? proctoringState : 'NOT_REQUIRED',
    agentRequired: enabled ? agentRequired : false,
    isProctoringActive: enabled ? isProctoringActive : false,
    showWarning: enabled ? showWarning : false,
    countdown: enabled ? countdown : 0,
    dismissWarning,
    sessionStatusSnapshot: enabled ? sessionStatusSnapshot : null,
    notificationPermission: 'agent-managed',
  }
}
